import { useState, useRef, useEffect } from 'react'
import { Button } from '../ui/Button'
import { User, Plus, Search, X } from 'lucide-react'

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
  gst_number?: string
}

interface CustomerSelectorProps {
  customers: Customer[]
  selectedCustomer: Customer | null
  onCustomerChange: (customerId: string) => void
  onAddCustomer: () => void
  className?: string
  hideLabel?: boolean
}

export const CustomerSelector = ({
  customers,
  selectedCustomer,
  onCustomerChange,
  onAddCustomer,
  className = '',
  hideLabel = false
}: CustomerSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Filter customers based on search term
  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase()
    return (
      customer.name.toLowerCase().includes(searchLower) ||
      customer.phone?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    )
  })

  // Add Walk-in as first option
  const allOptions = [
    { id: '', name: 'Walk-in', phone: undefined, email: undefined, gst_number: undefined },
    ...filteredCustomers
  ]

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true)
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev < allOptions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < allOptions.length) {
          handleSelectCustomer(allOptions[highlightedIndex].id)
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearchTerm('')
        break
    }
  }

  const handleSelectCustomer = (customerId: string) => {
    onCustomerChange(customerId)
    setIsOpen(false)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }

  const handleClearSelection = () => {
    onCustomerChange('')
    setSearchTerm('')
  }

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && isOpen) {
      const highlightedElement = document.getElementById(`customer-option-${highlightedIndex}`)
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [highlightedIndex, isOpen])

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {!hideLabel && (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Customer:</span>
        </div>
      )}

      <div className="flex items-center gap-1 relative flex-1" ref={dropdownRef}>
        <div className="relative flex-1">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={isOpen ? searchTerm : (selectedCustomer?.name || 'Walk-in')}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setIsOpen(true)
                setHighlightedIndex(-1)
              }}
              onFocus={() => {
                setIsOpen(true)
                setSearchTerm('')
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search customers..."
              className={`text-sm pl-7 pr-7 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full ${
                hideLabel ? 'h-7 py-1' : 'py-1.5'
              }`}
            />
            {selectedCustomer && (
              <button
                onClick={handleClearSelection}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Clear selection"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {allOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No customers found
                </div>
              ) : (
                allOptions.map((customer, index) => (
                  <div
                    key={customer.id || 'walk-in'}
                    id={`customer-option-${index}`}
                    onClick={() => handleSelectCustomer(customer.id)}
                    className={`px-3 py-2 cursor-pointer text-sm ${
                      highlightedIndex === index
                        ? 'bg-orange-100 dark:bg-orange-900/30'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                    } ${
                      selectedCustomer?.id === customer.id
                        ? 'bg-orange-50 dark:bg-orange-900/20'
                        : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {customer.name}
                    </div>
                    {(customer.phone || customer.email) && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {customer.phone && <span>{customer.phone}</span>}
                        {customer.phone && customer.email && <span> • </span>}
                        {customer.email && <span>{customer.email}</span>}
                      </div>
                    )}
                    {customer.gst_number && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                        GST: {customer.gst_number}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onAddCustomer}
          className={`${hideLabel ? 'h-7 w-7 p-0' : 'h-8 px-2'} flex-shrink-0`}
          title="Add New Customer"
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      {!hideLabel && selectedCustomer?.gst_number && (
        <div className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
          GST: {selectedCustomer.gst_number}
        </div>
      )}
    </div>
  )
}